import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Avatar from '../components/Avatar/Avatar';
import { MdIcon } from '../components/Icon/MdIcon';
import type { CallPhase } from './calls-context';
import './CallOverlay.css';

/**
 * The ringing screens: "Calling / Ringing / No answer" for the caller, and
 * the incoming call card with Decline / Answer for the receiver.
 *
 * An alertdialog over everything: a ringing call is the one thing that should
 * take focus. The receiver's focus lands on Answer; Escape declines an
 * incoming call and cancels an outgoing one, like the red button.
 */
export function CallOverlay({
  phase,
  onCancel,
  onAccept,
  onDecline,
}: {
  phase: CallPhase;
  onCancel: () => void;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const titleId = useId();
  const statusId = useId();
  const primaryRef = useRef<HTMLButtonElement>(null);
  const open = phase.kind !== 'idle';

  useEffect(() => {
    if (open) primaryRef.current?.focus();
  }, [open, phase.kind]);

  if (phase.kind === 'idle') return null;

  const isVoice = phase.type === 'audio';
  const kindLabel = isVoice ? 'voice' : 'video';
  const status =
    phase.kind === 'incoming'
      ? phase.groupName
        ? `Incoming group ${kindLabel} call · ${phase.groupName}`
        : `Incoming ${kindLabel} call`
      : phase.status === 'calling'
        ? 'Calling…'
        : phase.status === 'ringing'
          ? 'Ringing…'
          : (phase.endText ?? 'Call ended');
  const ended = phase.kind === 'outgoing' && phase.status === 'ended';

  return createPortal(
    <div
      className="call-overlay"
      onKeyDown={e => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        if (phase.kind === 'incoming') onDecline();
        else onCancel();
      }}>
      <div
        className="call-overlay__card"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={statusId}>
        <div className="call-overlay__avatar">
          <Avatar name={phase.peer.name} imageUrl={phase.peer.avatar} size={96} />
        </div>
        <h2 className="call-overlay__name" id={titleId}>
          {phase.peer.name}
        </h2>
        <p className="call-overlay__status" id={statusId} aria-live="polite">
          {status}
        </p>

        <div className="call-overlay__actions">
          {phase.kind === 'incoming' ? (
            <>
              <span className="call-overlay__action">
                <button
                  type="button"
                  className="call-fab call-fab--decline"
                  onClick={onDecline}
                  aria-label="Decline">
                  <MdIcon name="call_end" size={28} />
                </button>
                <span aria-hidden="true">Decline</span>
              </span>
              <span className="call-overlay__action">
                <button
                  ref={primaryRef}
                  type="button"
                  className="call-fab call-fab--accept"
                  onClick={onAccept}
                  aria-label={`Answer ${kindLabel} call`}>
                  <MdIcon name={isVoice ? 'call' : 'videocam'} size={28} />
                </button>
                <span aria-hidden="true">Answer</span>
              </span>
            </>
          ) : (
            <span className="call-overlay__action">
              <button
                ref={primaryRef}
                type="button"
                className="call-fab call-fab--decline"
                onClick={onCancel}
                disabled={ended}
                aria-label="Cancel call">
                <MdIcon name="call_end" size={28} />
              </button>
              <span aria-hidden="true">{ended ? '' : 'Cancel'}</span>
            </span>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
